import type { MaybeRefOrGetter, Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { computed, toValue, unref } from 'vue';
import { getApi } from './api';

/** V2 proxy: Unix seconds for date fields; shape aligns with device-api current-positions rows. */
export type V2CurrentPositionRow = {
  account_id: number;
  device_id: number;
  timestamp?: number;
  last_movement_time?: number;
  location?: [number, number];
  accuracy?: number | null;
  address?: string | null;
  battery?: number | null;
  fix?: string;
  speed?: number | null;
  humidity?: number | null;
  temperature?: number | null;
  [key: string]: unknown;
};

export default function getAccountCurrentPositionsV2(
  accountId: Ref<number | null | undefined> | number,
  deviceIds: Ref<number[]> | number[],
  options: {
    enabled: MaybeRefOrGetter<boolean>;
    refetchInterval?: MaybeRefOrGetter<number | false>;
  },
) {
  return useQuery({
    queryKey: [
      'v2',
      'account-current-positions',
      () => unref(accountId),
      () => unref(deviceIds),
    ],
    queryFn: async (): Promise<V2CurrentPositionRow[]> => {
      const { api, baseURL } = getApi();
      const aid = unref(accountId);
      const ids = unref(deviceIds);
      const res = await api.get(`/api/v2/accounts/${aid}/current-positions`, {
        baseURL,
        params: { device_ids: (ids ?? []).join(',') },
      });
      return (res.data?.data ?? []) as V2CurrentPositionRow[];
    },
    enabled: computed(() => {
      const aid = unref(accountId);
      const ids = unref(deviceIds);
      const paramsOk =
        aid != null && aid !== 0 && Array.isArray(ids) && ids.length > 0;
      return paramsOk && toValue(options.enabled);
    }),
    refetchInterval: computed(() => {
      const interval = options.refetchInterval;
      if (interval == null) return false;
      return toValue(interval);
    }),
  });
}
